import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import React from "react";
import { API_V1_BASE_URL } from "../../config/api";
import {
  Users,
  Building2,
  UserCheck,
  Eye,
  Download,
  Calendar,
  Tag,
  HardDrive,
  Loader2,
} from "lucide-react";

interface Asset {
  id: string;
  original_name: string;
  status: string;
  storage_location?: string;
  created_at?: string;
  expires_at?: string;
  file_size?: number;
  mime_type?: string;
}

export default function GuestSharePage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [error, setError] = useState("");
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  const [usageMap, setUsageMap] = useState<Record<string, any>>({});
  const [detailMap, setDetailMap] = useState<Record<string, any>>({});
  const [usageLoading, setUsageLoading] = useState<string | null>(null);
  const [shareExpiry, setShareExpiry] = useState<string | null>(null);

  // --- Pagination state ---
  const [page, setPage] = useState(1);
  const [limit] = useState(8);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    if (!token) return;

    const loadAssets = async () => {
      // Full-page loader only on the very first fetch; page changes use a lighter indicator
      if (page === 1 && assets.length === 0) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      try {
        const res = await axios.get(
          `${API_V1_BASE_URL}/guest/shares/${token}`,
          { params: { page, limit } },
        );

        if (res.data.success) {
          setAssets(res.data.data);
          setShareExpiry(res.data.expiresAt ?? null);
          if (res.data.pagination) {
            setTotal(res.data.pagination.total);
            setTotalPages(res.data.pagination.totalPages);
          }
        } else {
          setError(res.data.message);
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.message || "Unable to load shared assets.",
        );
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    };

    loadAssets();
  }, [token, page, limit]);

  const toggleUsage = async (assetId: string) => {
    if (expandedAssetId === assetId) {
      setExpandedAssetId(null);
      return;
    }
    setExpandedAssetId(assetId);

    if (usageMap[assetId]) return;

    setUsageLoading(assetId);
    try {
      const res = await axios.get(
        `${API_V1_BASE_URL}/guest/shares/${token}/assets/${assetId}`,
      );
      if (res.data.success) {
        setUsageMap((prev) => ({ ...prev, [assetId]: res.data.data.usage }));
        setDetailMap((prev) => ({ ...prev, [assetId]: res.data.data.asset }));
      }
    } catch (err) {
      setUsageMap((prev) => ({ ...prev, [assetId]: { error: true } }));
    } finally {
      setUsageLoading(null);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "—";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, { bg: string; text: string; dot: string }> =
      {
        Active: {
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          dot: "bg-emerald-500",
        },
        Inactive: {
          bg: "bg-slate-100",
          text: "text-slate-600",
          dot: "bg-slate-400",
        },
        Pending: {
          bg: "bg-amber-50",
          text: "text-amber-700",
          dot: "bg-amber-500",
        },
        Archived: {
          bg: "bg-blue-50",
          text: "text-blue-700",
          dot: "bg-blue-500",
        },
      };
    return (
      statusMap[status] || {
        bg: "bg-slate-100",
        text: "text-slate-600",
        dot: "bg-slate-400",
      }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="text-slate-600 font-medium text-lg">
            Loading shared assets...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">
            Access Error
          </h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <header className="bg-white border-b border-slate-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 tracking-tight">
                  Guest Panel
                </h1>
                <p className="text-xs text-slate-500 -mt-0.5">Guest Share</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full border border-slate-200/60">
                <span className="font-medium text-slate-700">
                  {total}
                </span>{" "}
                assets
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200/70 bg-slate-50/50 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-slate-600" />
                Shared Assets
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {total} {total === 1 ? "asset" : "assets"}{" "}
                shared with you
              </p>
            </div>
          </div>

          {assets.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HardDrive className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-700">
                No assets found
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                No assets have been shared with this link.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-200/70">
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 w-[40%]">
                      Asset
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 hidden sm:table-cell w-[20%]">
                      Type
                    </th>
                    <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell w-[20%]">
                      Size
                    </th>
                    <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider px-6 py-3.5 w-[20%]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {assets.map((asset) => {
                    return (
                      <React.Fragment key={asset.id}>
                        <tr className="hover:bg-slate-50/60 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-blue-100/60">
                                <HardDrive className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800 text-sm truncate max-w-[140px] sm:max-w-[200px]">
                                  {asset.original_name}
                                </p>
                                <p className="text-xs text-slate-400 truncate max-w-[120px] sm:hidden">
                                  {asset.mime_type || "File"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 hidden sm:table-cell">
                            <span className="text-sm text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full text-xs">
                              {asset.mime_type?.split("/")[1]?.toUpperCase() ||
                                "File"}
                            </span>
                          </td>
                          <td className="px-6 py-4 hidden md:table-cell">
                            <span className="text-sm text-slate-600 font-mono">
                              {formatFileSize(asset.file_size)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {asset.storage_location ? (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => toggleUsage(asset.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm hover:shadow"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  {expandedAssetId === asset.id
                                    ? "Hide"
                                    : "View"}
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 italic">
                                No link
                              </span>
                            )}
                          </td>
                        </tr>
                        {expandedAssetId === asset.id && (
                          <tr className="bg-slate-50/40">
                            <td colSpan={4} className="px-6 py-4">
                              {usageLoading === asset.id ? (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Loading usage...
                                </div>
                              ) : usageMap[asset.id]?.error ? (
                                <p className="text-sm text-red-500">
                                  Failed to load usage details.
                                </p>
                              ) : (
                                <div className="space-y-5">
                                  {detailMap[asset.id]?.mime_type?.startsWith("image/") && (
                                    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                      {detailMap[asset.id]?.preview_url ? (
                                        <img
                                          src={detailMap[asset.id].preview_url}
                                          alt={asset.original_name}
                                          className="mx-auto max-h-[520px] w-auto max-w-full rounded-lg object-contain"
                                        />
                                      ) : (
                                        <div className="flex min-h-48 items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-500">
                                          Image preview is unavailable. The asset may be archived.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {(detailMap[asset.id]?.mime_type?.startsWith("video/") ||
                                    ["mp4", "mpeg", "mpg", "mov", "webm", "m4v"].includes(
                                      String(detailMap[asset.id]?.file_extension || "")
                                        .replace(/^\./, "")
                                        .toLowerCase(),
                                    )) && (
                                    <div className="rounded-xl border border-slate-200 bg-black p-3 shadow-sm">
                                      {detailMap[asset.id]?.preview_url ? (
                                        <video
                                          controls
                                          playsInline
                                          preload="metadata"
                                          className="mx-auto max-h-[520px] w-full rounded-lg bg-black"
                                        >
                                          <source
                                            src={detailMap[asset.id].preview_url}
                                            type={detailMap[asset.id].mime_type || "video/mp4"}
                                          />
                                          Your browser does not support video playback.
                                        </video>
                                      ) : (
                                        <div className="flex min-h-48 items-center justify-center rounded-lg bg-slate-900 text-sm text-slate-300">
                                          Video preview is unavailable. The asset may be archived or awaiting restoration.
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">
                                      Campaigns
                                    </p>
                                    {usageMap[asset.id]?.campaigns?.items
                                      ?.length ? (
                                      usageMap[asset.id].campaigns.items.map(
                                        (c: any) => (
                                          <p
                                            key={c.id}
                                            className="text-slate-600"
                                          >
                                            {c.name} ({c.status})
                                          </p>
                                        ),
                                      )
                                    ) : (
                                      <p className="text-slate-400 italic">
                                        Not mapped
                                      </p>
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-700 mb-1">
                                      Products
                                    </p>
                                    {usageMap[asset.id]?.products?.items
                                      ?.length ? (
                                      usageMap[asset.id].products.items.map(
                                        (p: any) => (
                                          <p
                                            key={p.id}
                                            className="text-slate-600"
                                          >
                                            {p.name}
                                          </p>
                                        ),
                                      )
                                    ) : (
                                      <p className="text-slate-400 italic">
                                        Not mapped
                                      </p>
                                    )}
                                  </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-200/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <span className="font-medium text-slate-700">Rows per page:</span>
              <span className="bg-white border border-slate-200 rounded-lg px-3 py-1 text-slate-700 font-medium">
                {limit}
              </span>
              <span className="hidden sm:inline">
                <span className="font-medium text-slate-700">
                  {total === 0 ? 0 : (page - 1) * limit + 1}-
                  {Math.min(page * limit, total)}
                </span>{" "}
                of {total}
                {pageLoading && (
                  <Loader2 className="inline w-3.5 h-3.5 ml-2 animate-spin text-slate-400" />
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1 || pageLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white text-sm"
              >
                Prev
              </button>
              <span className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium border border-blue-600">
                {page}
              </span>
              <button
                disabled={page >= totalPages || pageLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white text-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
